import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { useMetadata } from '@/hooks/useMetadata';

const ImageInquiryDialog = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  loading = false,
  cardType = 'NON_PSA_SINGLE_CARD'
}) => {
  const { t, i18n } = useTranslation();
  const [selectedTypes, setSelectedTypes] = useState([]);

  // Use shared metadata hook - data is prefetched on app start and cached for 1 hour
  const { data: metadata, isLoading: metadataLoading } = useMetadata();

  const requestTypes = metadata?.inquiryTypes?.[cardType] || [];

  useEffect(() => {
    if (isOpen) {
      setSelectedTypes([]);
    }
  }, [isOpen]);

  const handleTypeToggle = (code) => {
    setSelectedTypes(prev => 
      prev.includes(code) 
        ? prev.filter(t => t !== code)
        : [...prev, code]
    );
  };

  const handleSubmit = () => {
    if (selectedTypes.length > 0) {
      onSubmit(selectedTypes);
    }
  };

  const handleClose = () => {
    setSelectedTypes([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-[400px] bg-white rounded-xl flex flex-col justify-start items-center overflow-hidden">
        <div className="self-stretch px-6 pt-8 pb-6 flex flex-col justify-start items-center gap-4">
          <div className="self-stretch text-center justify-start text-[#111111] text-lg font-bold leading-[27px]">
            {t('psaInventory.requestDialog.title')}
          </div>
          <div className="self-stretch flex flex-col justify-start items-start">
            {metadataLoading ? (
              <div className="self-stretch h-[47px] flex items-center justify-center text-[#666666]">
                {t('common.loading')}
              </div>
            ) : (
              requestTypes.map((type) => (
                <div 
                  key={type.code}
                  className="w-full py-2 flex justify-center items-center relative border-b border-[#eeeeee] cursor-pointer"
                  onClick={() => handleTypeToggle(type.code)}
                >
                  <Checkbox 
                    checked={selectedTypes.includes(type.code)}
                    size={5}
                  />
                  <div className="flex-1 ml-4 text-[#111111] text-base font-normal leading-normal">
                    {i18n.language === 'ja' ? type.localizedName : type.name}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
          <div className="self-stretch flex justify-between items-center gap-3">
            <Button 
              variant="default" 
              className="flex-1"
              onClick={handleClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              variant="primary" 
              className="flex-1"
              onClick={handleSubmit}
              disabled={selectedTypes.length === 0 || loading || metadataLoading}
            >
              {loading ? t('common.loading') : t('common.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageInquiryDialog;